import { useCallback, useEffect, useState } from 'react';
import Asset from '../../models/asset';
import Folder from '../../models/folder';
import useDownloadItems from './useDownloadItems';
import useReduxUserPreferences from './useReduxUsePreferences';

export type UseDownloadItemsModalProps = {
	assets: Asset[];
	folders: Folder[];
	shareRef?: string;
	onClose: ( confirmed: boolean ) => void;
};

export const useDownloadItemsModal = ( { assets, folders, shareRef, onClose }: UseDownloadItemsModalProps ) => {
	const {
		currentUserPrefferedImageResolution, updateImageResolutionPreference
	} = useReduxUserPreferences( { shareRef } );
	const downloadItems = useDownloadItems( { shareRef, onDownloadFinish: () => onClose( true ) } );

	const [ appendRoundNumber, setAppendRoundNumber ] = useState( false );
	const [ downloadAllRounds, setDownloadAllRounds ] = useState( false );
	const [ imageSize, setImageSize ] = useState<string | null>( currentUserPrefferedImageResolution );

	const isDownloadingMultipleItems = ( assets.length + folders.length ) > 1;
	const isDownloadingOneAsset = assets.length === 1 && folders.length === 0;
	const assethasMultipleRounds = assets[ 0 ]?.roundsCount > 1;

	const showDownloadAllRounds = isDownloadingOneAsset && assethasMultipleRounds;

	const hasUnpreviewableAsset = assets.some( asset => asset.unpreviewable );
	const hasDocumentAsset = assets.some( asset => asset.isDocument );
	const requiresOriginalSize = hasDocumentAsset || hasUnpreviewableAsset;

	const onDownloadClick = useCallback( () => {
		downloadItems( {
			assets,
			folders,
			downloadAllRounds,
			withRoundNumber: downloadAllRounds || appendRoundNumber,
			imageSize,
			unpreviewable: hasUnpreviewableAsset
		} );
	}, [ downloadItems, appendRoundNumber, downloadAllRounds, imageSize, assets, folders, hasUnpreviewableAsset ] );

	useEffect( () => {
		if ( imageSize ) {
			updateImageResolutionPreference( imageSize );
		}
	}, [ imageSize, updateImageResolutionPreference ] );

	return {
		appendRoundNumber,
		downloadAllRounds,
		imageSize,
		setAppendRoundNumber,
		setDownloadAllRounds,
		setImageSize,
		isDownloadingMultipleItems,
		isDownloadingOneAsset,
		showDownloadAllRounds,
		requiresOriginalSize,
		hasUnpreviewableAsset,
		onDownloadClick
	};
};

export default useDownloadItemsModal;
